import { useMemo, useState } from 'react';
import { ArrowRight, CircleDot, Film, LayoutGrid, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { TkticSelect } from '@/components/ui/TkticSelect';
import { CoachModal } from '@/features/coach/components/CoachModal';
import { matchLabel } from '@/features/coach-review/components/MatchSlotPicker';
import { SLOT_LABELS, defaultSlotForMatch, isSlotValidForMatch, slotsForMatch } from '@/features/matches/matchSlots';
import { useMatches } from '@/features/matches/useMatches';
import type { ResourceKind } from '@/lib/types';
import { useScopeStore } from '@/stores/scope';
import type { ResourceEditorSlot } from './useResourceEditor';

export const RESOURCE_KIND_LABEL:Record<ResourceKind, string> = { moment:'Moment', clip:'Clip', tactical_board:'Tactical board' };
export const RESOURCE_KIND_ICON:Record<ResourceKind, LucideIcon> = { moment:CircleDot, clip:Film, tactical_board:LayoutGrid };
const KINDS:[ResourceKind, string][] = [
  ['moment', 'One frame of a recording, with drawing.'],
  ['clip', 'A range of a recording, up to two minutes.'],
  ['tactical_board', 'A drawn shape or pattern, no video.'],
];

/** Kind first, then the match and recording for a video kind (F1 step 2).
 * Matches come from the active team and season only. */
export function ResourceKindChooser({ kind:initialKind, matchId:initialMatch, note, onOpen, onClose }:{
  kind?:ResourceKind; matchId?:string; note?:string;
  onOpen(kind:ResourceKind, matchId?:string, slot?:ResourceEditorSlot):void; onClose():void;
}) {
  const seasonId = useScopeStore(state => state.seasonId);
  const matches = useMatches();
  const options = useMemo(() => (matches.data ?? []).filter(match => (match.season_id ?? null) === seasonId), [matches.data, seasonId]);
  const [kind, setKind] = useState<ResourceKind>(initialKind ?? 'moment');
  const [matchId, setMatchId] = useState(initialMatch ?? '');
  const [slot, setSlot] = useState('');
  const match = options.find(row => row.id === matchId) ?? options[0];
  const chosenSlot = match && isSlotValidForMatch(match, slot) ? slot as ResourceEditorSlot : match ? defaultSlotForMatch(match) : undefined;
  const video = kind !== 'tactical_board';
  const ready = !video || !!match && !!chosenSlot;
  return <CoachModal title="New resource" onClose={onClose} data-testid="resource-kind-chooser"
    footer={<div className="flex justify-end gap-2">
      <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
      <Button type="button" disabled={!ready} data-testid="resource-kind-chooser-open"
        onClick={() => onOpen(kind, video ? match?.id : undefined, video ? chosenSlot : undefined)}>Open editor<ArrowRight aria-hidden/></Button>
    </div>}>
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-3" role="group" aria-label="Resource kind">
        {KINDS.map(([value, description]) => {
          const Icon = RESOURCE_KIND_ICON[value], selected = kind === value;
          return <button key={value} type="button" aria-pressed={selected} onClick={() => setKind(value)}
            data-testid={`resource-kind-${value}`}
            className={`flex flex-col gap-2 rounded-inset border p-4 text-left text-ink transition-colors hover:border-accent ${selected ? 'border-accent bg-accent/10' : 'border-rule bg-surface'}`}>
            <Icon size={24} aria-hidden className={selected ? 'text-accent-text' : 'text-ink-muted'}/>
            <strong className="font-display text-[17px] font-medium">{RESOURCE_KIND_LABEL[value]}</strong>
            <span className="text-xs leading-normal text-ink-muted">{description}</span>
          </button>;
        })}
      </div>
      {video && <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5"><p className="eyebrow">Match</p>
          <TkticSelect ariaLabel="Match" value={match?.id ?? ''} onChange={setMatchId} disabled={!options.length}
            placeholder={matches.isLoading ? 'Loading matches…' : 'No matches this season'}
            options={options.map(row => ({ value:row.id, label:matchLabel(row) }))}/></div>
        <div className="flex flex-col gap-1.5"><p className="eyebrow">Recording</p>
          <TkticSelect ariaLabel="Recording" value={chosenSlot ?? ''} onChange={setSlot} disabled={!match}
            options={slotsForMatch(match).map(value => ({ value, label:SLOT_LABELS[value] }))}/></div>
      </div>}
      {note && <p className="text-xs text-ink-muted">{note}</p>}
    </div>
  </CoachModal>;
}
